"use client"

import type { FC } from "react"
import { useState } from "react"
import {
	IconChevronDownOutline18 as ChevronDown,
	IconChevronUpOutline18 as ChevronUp,
} from "nucleo-ui-outline-18"

import type {
	ApiAuthDoc,
	ParamDoc,
	SchemaNode,
} from "@productlane/lib/src/api-reference"
import { schemaTypeLabel } from "@productlane/lib/src/api-reference/schema-tree"
import { cn } from "@productlane/lib/src/cn"

import { Badge } from "../badge"
import { Button } from "../button"
import { useApiReferenceEnv, usePointerClass } from "./env"
import { InlineMarkdown } from "./inline-markdown"

export const ROW_DIVIDER =
	"border-t border-gray-200/80 first:border-t-0 dark:border-white/[0.07]"

/** Rows of fields; nested objects and variants open on demand so long schemas stay readable. */
export const SchemaFields: FC<{ fields: SchemaNode[]; depth?: number }> = ({
	fields,
	depth = 0,
}) => (
	<ul className="flex flex-col">
		{fields.map((field, index) => (
			<SchemaRow
				key={`${field.name ?? "item"}-${index}`}
				node={field}
				depth={depth}
			/>
		))}
	</ul>
)

export const ParamFields: FC<{ params: ParamDoc[]; showLocation: boolean }> = ({
	params,
	showLocation,
}) => (
	<ul className="flex flex-col">
		{params.map((param) => (
			<SchemaRow
				key={`${param.in}:${param.name}`}
				node={{
					...param.schema,
					name: param.name,
					required: param.required,
					deprecated: param.deprecated ?? param.schema.deprecated,
					description: param.description ?? param.schema.description,
				}}
				location={showLocation ? param.in : undefined}
				depth={0}
			/>
		))}
	</ul>
)

/** Each credential as the field a caller sends: `Authorization`, `string`, `header`. */
export const AuthFields: FC<{
	auth: (ApiAuthDoc & { required?: boolean })[]
}> = ({ auth }) => (
	<ul className="flex flex-col">
		{auth.map((scheme) => (
			<SchemaRow
				key={scheme.name}
				node={{
					name: scheme.field,
					type: "string",
					required: scheme.required,
					description: scheme.description ?? scheme.label,
				}}
				location={scheme.in}
				depth={0}
			/>
		))}
	</ul>
)

export const SchemaRow: FC<{
	node: SchemaNode
	depth: number
	location?: ParamDoc["in"] | ApiAuthDoc["in"]
}> = ({ node, depth, location }) => {
	const { t } = useApiReferenceEnv()
	const pointer = usePointerClass()
	const [isOpen, setIsOpen] = useState(false)
	const hasChildren = !!node.children?.length
	const hasVariants = !!node.variants?.length

	return (
		<li className={cn("flex flex-col gap-2 py-4", ROW_DIVIDER)}>
			<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
				{node.name && (
					<code className="font-mono text-[13.5px] font-semibold text-primary">
						{node.name}
					</code>
				)}
				<Badge variant="light-gray" size="code">
					{schemaTypeLabel(node)}
				</Badge>
				{location && (
					<Badge variant="light-gray" size="code">
						{location}
					</Badge>
				)}
				{node.required && (
					<Badge variant="red" size="code">
						{t("apiReference.required", "required")}
					</Badge>
				)}
				{node.deprecated && (
					<Badge variant="amber" size="code">
						{t("apiReference.deprecated", "Deprecated")}
					</Badge>
				)}
			</div>
			{node.description && (
				<InlineMarkdown
					text={node.description}
					className="text-[14px] leading-[1.6] text-secondary"
				/>
			)}
			{node.enumValues && node.enumValues.length > 0 && (
				<ValueBadges
					label={t("apiReference.oneOf", "One of")}
					values={node.enumValues}
				/>
			)}
			{node.defaultValue !== undefined && (
				<ValueBadges
					label={t("apiReference.defaultValue", "Default")}
					values={[node.defaultValue]}
				/>
			)}
			{(hasChildren || hasVariants) && (
				<div className="flex flex-col gap-2 pt-1">
					<Button
						variant="outline-subtle"
						size="sm"
						onClick={() => setIsOpen((open) => !open)}
						aria-expanded={isOpen}
						iconRight={isOpen ? ChevronUp : ChevronDown}
						className={cn("self-start text-[12.5px]", pointer)}
					>
						{isOpen
							? t("apiReference.hideFields", "Hide fields")
							: t("apiReference.showFields", "Show fields")}
					</Button>
					{isOpen && hasChildren && (
						<div className="rounded-xl px-4 ring-1 ring-black/[0.07] dark:ring-white/[0.08]">
							<SchemaFields fields={node.children ?? []} depth={depth + 1} />
						</div>
					)}
					{isOpen && hasVariants && (
						<div className="flex flex-col rounded-xl ring-1 ring-black/[0.07] dark:ring-white/[0.08]">
							{node.variants?.map((variant) => (
								<div
									key={variant.label}
									className={cn("flex flex-col px-4 pt-3", ROW_DIVIDER)}
								>
									<span className="font-mono text-[12.5px] font-medium text-secondary">
										{variant.label}
									</span>
									{variant.node.children?.length ? (
										<SchemaFields
											fields={variant.node.children}
											depth={depth + 1}
										/>
									) : (
										<div className="pb-3 pt-2">
											<Badge variant="light-gray" size="code">
												{schemaTypeLabel(variant.node)}
											</Badge>
										</div>
									)}
								</div>
							))}
						</div>
					)}
				</div>
			)}
		</li>
	)
}

const ValueBadges: FC<{ label: string; values: string[] }> = ({
	label,
	values,
}) => (
	<div className="flex flex-wrap items-center gap-1.5 text-[13px] text-tertiary">
		<span className="mr-0.5">{label}:</span>
		{values.map((value, index) => (
			<Badge key={`${value}-${index}`} variant="default" size="code">
				{value}
			</Badge>
		))}
	</div>
)
